/**
 * Demo Analytics Utility
 * 
 * This is a demonstration analytics implementation that logs events to the console.
 * In production, replace console.log calls with actual analytics service calls
 * (Google Analytics, Mixpanel, Amplitude, etc.)
 */

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
}

export interface PageViewData {
  path: string;
  title?: string;
  referrer?: string;
  timestamp: string;
}

export interface UserProperties {
  userId?: string;
  userRole?: string;
  merchantId?: string;
  [key: string]: any;
}

class Analytics {
  private isEnabled: boolean = true;
  private sessionId: string;
  private sessionStart: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStart = Date.now();
    this.init();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private init() {
    console.log("📊 Analytics Service Initialized");
    console.log(`📊 Session ID: ${this.sessionId}`);
  }

  /**
   * Track page view
   */
  trackPageView(data: PageViewData) {
    if (!this.isEnabled) return;

    const eventData = {
      type: "pageview",
      sessionId: this.sessionId,
      ...data,
    };

    console.log("📊 Page View:", eventData);

    // Example: Send to Google Analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "page_view", {
        page_path: data.path,
        page_title: data.title,
      });
    }

    // Example: Send to custom backend
    // this.sendToBackend('/api/analytics/pageview', eventData);
  }

  /**
   * Track custom event
   */
  trackEvent(event: AnalyticsEvent) {
    if (!this.isEnabled) return;

    const eventData = {
      type: "event",
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      ...event,
    };

    console.log("📊 Event:", eventData);

    // Example: Send to Google Analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
    }

    // Example: Send to custom backend
    // this.sendToBackend('/api/analytics/event', eventData);
  }

  /**
   * Track user properties
   */
  setUserProperties(properties: UserProperties) {
    if (!this.isEnabled) return;

    console.log("📊 User Properties:", properties);

    // Example: Send to Google Analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("set", "user_properties", properties);
    }

    // Example: Send to custom backend
    // this.sendToBackend('/api/analytics/user', properties);
  }

  /**
   * Track transaction/conversion
   */
  trackTransaction(transactionData: {
    transactionId: string;
    amount: number;
    currency?: string;
    items?: any[];
  }) {
    if (!this.isEnabled) return;

    const eventData = {
      type: "transaction",
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      ...transactionData,
    };

    console.log("📊 Transaction:", eventData);

    // Example: Send to Google Analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "purchase", {
        transaction_id: transactionData.transactionId,
        value: transactionData.amount,
        currency: transactionData.currency || "SAR",
        items: transactionData.items,
      });
    }
  }

  /**
   * Track error
   */
  trackError(error: Error | string, context?: Record<string, any>) {
    if (!this.isEnabled) return;

    const errorData = {
      type: "error",
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      error: typeof error === "string" ? error : error.message,
      stack: typeof error === "string" ? undefined : error.stack,
      context,
    };

    console.error("📊 Error:", errorData);

    // Example: Send to error tracking service
    // this.sendToBackend('/api/analytics/error', errorData);
  }

  /**
   * Track timing/performance
   */
  trackTiming(category: string, variable: string, time: number, label?: string) {
    if (!this.isEnabled) return;

    const timingData = {
      type: "timing",
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      category,
      variable,
      time,
      label,
    };

    console.log("📊 Timing:", timingData);

    // Example: Send to Google Analytics
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "timing_complete", {
        name: variable,
        value: time,
        event_category: category,
        event_label: label,
      });
    }
  }

  /**
   * Get session duration
   */
  getSessionDuration(): number {
    return Date.now() - this.sessionStart;
  }

  /**
   * End session
   */
  endSession() {
    const duration = this.getSessionDuration();
    console.log(`📊 Session Ended. Duration: ${Math.round(duration / 1000)}s`);

    this.trackEvent({
      category: "session",
      action: "end",
      value: Math.round(duration / 1000),
    });
  }

  /**
   * Enable/disable analytics
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    console.log(`📊 Analytics ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Send data to backend (example implementation)
   * Uncomment the method calls above to enable backend tracking
   * @private - Reserved for future use
   */
  // @ts-ignore - Demo method for future implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async sendToBackend(endpoint: string, data: any) {
    try {
      // Uncomment and configure for production use
      // await fetch(endpoint, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(data),
      // });
    } catch (error) {
      console.error("Analytics send error:", error);
    }
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Convenience functions
export const trackPageView = (path: string, title?: string) => {
  analytics.trackPageView({
    path,
    title,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
  });
};

export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  analytics.trackEvent({ category, action, label, value });
};

export const trackError = (error: Error | string, context?: Record<string, any>) => {
  analytics.trackError(error, context);
};

export const setUserProperties = (properties: UserProperties) => {
  analytics.setUserProperties(properties);
};

export default analytics;
